import { ensureAnonSession, supabase } from '../lib/supabase'
import { isStaffPreview } from './previewMode'

/**
 * Shared show session. Mirrors the project-joining half of
 * utils/sharedReview.ts exactly, one level up: a show has no deliverables,
 * change requests or approvals of its own, so none of that machinery is
 * needed here — only "exchange this link's slug and token for the show".
 */

/** One joined show per browser session, keyed by slug. */
const joined = new Map<string, string>()
const pending = new Map<string, Promise<string | null>>()

function warn(what: string, error: { message?: string; code?: string } | null | unknown) {
  if (!error) return
  const detail = error as { message?: string; code?: string }
  console.warn(
    `[supabase] ${what} failed: ${detail.message ?? String(error)}${
      detail.code ? ` (${detail.code})` : ''
    }`,
  )
}

/** The access token on the current client link, e.g. /show/<slug>?access=<uuid>. */
function accessTokenFromUrl(): string {
  if (typeof window === 'undefined') return ''
  const params = new URLSearchParams(window.location.search)
  return params.get('access') ?? params.get('token') ?? ''
}

/** The show UUID for this slug, joining first if necessary. */
async function joinShow(
  slug: string,
  displayName: string,
  suppliedToken = '',
): Promise<string | null> {
  if (!supabase || !slug) return null

  const existing = joined.get(slug)
  if (existing) return existing

  const inFlight = pending.get(slug)
  if (inFlight) return inFlight

  const request = (async () => {
    /* Staff previewing a show link never join it, for the same reason as a
       project preview: joining records the viewer as a member. */
    const token = isStaffPreview() ? '' : suppliedToken || accessTokenFromUrl()
    let showId: string | null = null

    if (token) {
      await ensureAnonSession()

      const { data, error } = await supabase.rpc('join_show', {
        show_slug: slug,
        show_token: token,
        display_name: displayName || 'Client',
      })
      warn('join_show', error)

      showId =
        typeof data === 'string'
          ? data
          : Array.isArray(data)
            ? (data[0]?.id ?? null)
            : ((data as { id?: string } | null)?.id ?? null)
    } else {
      /* Studio side: no link token, so resolve the show directly. Signed-in
         staff are permitted this by the show's existing RLS policies. */
      const { data, error } = await supabase
        .from('shows')
        .select('id')
        .eq('slug', slug)
        .maybeSingle()
      warn('show lookup', error)
      showId = (data?.id as string | undefined) ?? null
    }

    if (!showId) return null
    joined.set(slug, showId)
    return showId
  })()

  pending.set(slug, request)
  const result = await request
  pending.delete(slug)
  return result
}

/**
 * Joins the show for this link. Called when the client enters their name;
 * safe to call repeatedly.
 */
export async function openShowSession(
  slug: string,
  displayName: string,
  suppliedToken = '',
): Promise<string | null> {
  return joinShow(slug, displayName, suppliedToken)
}

/** Forgets the joined session — used when the remembered name is cleared. */
export function closeShowSession(): void {
  joined.clear()
  pending.clear()
}
